import { ref } from 'vue';
import { v4 } from 'uuid';

import type {
  LazySelectFetchOptions,
  PaginatedResponse,
} from '@/components/forms/hooks/useLazySelect';
import { usePointOfSales } from '@/views/pointofsales/hooks/usePointOfSales';
import { useTickets } from '@/views/tickets/hooks/useTickets';
import type {
  PointOfSaleFilterInnerV2Dto,
  PointOfSaleSimpleV2Dto,
  TicketSimpleV2Dto,
} from '@/openapi';

export function useClassificationLookups() {
  const { fetchTicket, fetchTicketsSimple } = useTickets();
  const { fetchPointOfSalesSimple, fetchPointOfSale } = usePointOfSales();

  // Store all tickets: temp tickets (version 0) + loaded tickets from lazy select
  const ticketsAlreadyExisting = ref<(TicketSimpleV2Dto & { version?: number })[]>([]);

  const tempTicketIds = new Map<number, string>();

  const generateTempTicket = (index: number, fileName: string) => {
    // Check if temp ticket already exists for this index
    if (tempTicketIds.has(index)) {
      const existingId = tempTicketIds.get(index);
      return existingId;
    }

    const newTicketId = v4();
    tempTicketIds.set(index, newTicketId);

    const now = new Date().toISOString();
    const tempTicket = {
      id: newTicketId,
      createdAt: now,
      name: `${fileName}-${newTicketId}`,
      number: `tickerNR-${newTicketId}`,
      version: 0,
    };

    ticketsAlreadyExisting.value.push(tempTicket);

    return newTicketId;
  };

  const removeTempTicket = (index: number) => {
    const uuid = tempTicketIds.get(index);
    if (!uuid) {
      return;
    }

    // remove ticket from lookup list
    ticketsAlreadyExisting.value = ticketsAlreadyExisting.value.filter((x) => x.id !== uuid);

    tempTicketIds.delete(index);
  };

  const createFetchPointOfSalesForLazySelect = (ticketId?: string) => {
    return async (
      options: LazySelectFetchOptions,
    ): Promise<PaginatedResponse<PointOfSaleSimpleV2Dto> | undefined> => {
      let filter: PointOfSaleFilterInnerV2Dto | undefined = undefined;

      if (ticketId) {
        // Check if selected ticket is a temp ticket (version 0)
        const version0Ids = ticketsAlreadyExisting.value
          .filter((t) => t.version === 0)
          .map((t) => t.id);

        // Only apply filter for non-temp tickets
        if (!version0Ids.includes(ticketId)) {
          // Use server-side filtering: filter POS by ticket ID
          // Prisma syntax: for array relations, use 'some' to match any item
          filter = {
            tickets: {
              some: {
                id: {
                  equals: ticketId,
                },
              },
            },
          };
        }
      }

      const response = await fetchPointOfSalesSimple(
        undefined,
        filter,
        options.searchString,
        options.page,
        options.limit,
      );

      return response;
    };
  };

  /**
   * Fetch tickets for lazy select dropdown
   */
  const fetchTicketsForLazySelect = async (
    options: LazySelectFetchOptions,
  ): Promise<PaginatedResponse<TicketSimpleV2Dto> | undefined> => {
    const response = await fetchTicketsSimple(
      undefined,
      options.searchString,
      options.page,
      options.limit,
    );

    if (!response?.data) return response;

    // Add fetched tickets to ticketsAlreadyExisting (avoid duplicates, keep temp tickets)
    response?.data.forEach((ticket) => {
      const exists = ticketsAlreadyExisting.value.some((t) => t.id === ticket.id);
      if (!exists) {
        ticketsAlreadyExisting.value.push(ticket);
      }
    });

    // Include temp tickets (version 0) in the first page of results only
    let finalData = response.data;
    if (options.page === 0 || options.page === 1) {
      const tempTickets = ticketsAlreadyExisting.value.filter((t) => t.version === 0);

      if (tempTickets.length > 0) {
        // Remove any temp tickets that might already be in response.data (shouldn't happen but be safe)
        const tempTicketIds = tempTickets.map((t) => t.id);
        const apiTicketsOnly = response.data.filter((t) => !tempTicketIds.includes(t.id));
        // Prepend temp tickets to the results
        finalData = [...tempTickets, ...apiTicketsOnly];
      }
    }

    return {
      ...response,
      data: finalData,
    };
  };

  /**
   * Fetch a single point of sale by ID
   */
  const fetchSinglePointOfSaleById = async (
    id: string,
  ): Promise<PointOfSaleSimpleV2Dto | null | undefined> => {
    try {
      return await fetchPointOfSale(id);
    } catch (err) {
      console.error('Error fetching single point of sale:', err);
      return null;
    }
  };

  /**
   * Fetch a single ticket by ID
   */
  const fetchSingleTicketById = async (id: string): Promise<TicketSimpleV2Dto | undefined> => {
    // Check if it's a temp ticket (version 0) first
    const tempTicket = ticketsAlreadyExisting.value.find((t) => t.id === id && t.version === 0);
    if (tempTicket) {
      return tempTicket; // Temp tickets don't have all TicketSimple fields, but it's OK for display
    }

    // Otherwise fetch from API
    return await fetchTicket(id);
  };

  return {
    ticketsAlreadyExisting,
    generateTempTicket,
    removeTempTicket,
    createFetchPointOfSalesForLazySelect,
    fetchTicketsForLazySelect,
    fetchSinglePointOfSaleById,
    fetchSingleTicketById,
  };
}
